
import React from 'react';
import { InterviewRequestResponse } from '@/services/interview/requests/types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format, isValid, parseISO } from 'date-fns';
import { Calendar, Clock, Mail, Link } from 'lucide-react';
import { toast } from 'sonner';

interface ViewRequestDialogProps {
  request: InterviewRequestResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

const ViewRequestDialog: React.FC<ViewRequestDialogProps> = ({
  request,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  
  if (!request) return null;
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = parseISO(dateString);
    return isValid(date) ? format(date, 'dd.MM.yyyy HH:mm') : t('interview.invalidDate');
  };

  const getStatusBadge = (status: string, expiresAt: string | null) => {
    if (status === 'completed') {
      return <Badge className="bg-green-500 text-white">{t('interview.completed')}</Badge>;
    }
    
    if (status === 'expired' || (expiresAt && new Date(expiresAt) < new Date())) {
      return <Badge className="bg-red-500 text-white">{t('interview.expired')}</Badge>;
    }
    
    return <Badge className="bg-amber-500 text-white">{t('interview.pending')}</Badge>;
  };

  const copySubmissionLink = () => {
    const url = `${window.location.origin}/interview-submission/${request.request_link}`;
    navigator.clipboard.writeText(url);
    toast.success(t('interview.linkCopied'));
    
    // Log for debugging
    console.log('Copying interview submission link from dialog:', {
      url,
      requestLink: request.request_link,
      question: request.question,
      questionLength: request.question?.length || 0
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('interview.requests')}</DialogTitle>
          <DialogDescription>
            {t('interview.viewDetails')} - {request.interviewee_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-lg">{request.interviewee_name}</h3>
            {getStatusBadge(request.status, request.expires_at)}
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>{request.interviewee_email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{t('interview.createdAt')}: {formatDate(request.created_at)}</span>
            </div>
            {request.expires_at && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{t('interview.expiresAt')}: {formatDate(request.expires_at)}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">{t('interview.question')}:</h4>
            <p className="bg-gray-50 p-3 rounded border border-gray-200">{request.question}</p>
          </div>

          {request.context && (
            <div className="space-y-2">
              <h4 className="font-medium">{t('interview.context')}:</h4>
              <p className="bg-gray-50 p-3 rounded border border-gray-200">{request.context}</p>
            </div>
          )}
          
          {request.personalized_message && (
            <div className="space-y-2">
              <h4 className="font-medium">{t('interview.personalizedMessage')}:</h4>
              <p className="bg-gray-50 p-3 rounded border border-gray-200">{request.personalized_message}</p>
            </div>
          )}

          <div className="space-y-2">
            <h4 className="font-medium">{t('interview.submissionLink')}:</h4>
            <div className="flex items-center gap-2 bg-gray-50 p-3 rounded border border-gray-200">
              <code className="text-xs break-all flex-1">
                {`${window.location.origin}/interview-submission/${request.request_link}`}
              </code>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={copySubmissionLink}>
            <Link className="w-4 h-4 mr-2" />
            {t('interview.copyLink')}
          </Button>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewRequestDialog;
